import { includes } from 'lodash';
import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { BsSearch } from '@react-icons/all-files/bs/BsSearch';
import { Dropdown, DropdownMenu, DropdownToggle, Input } from 'reactstrap';
import { bind } from '../Utils/Bind';
import { deepStringFind } from '../Utils/DeepStringFind';
import { componentAutoReaction } from '../Utils/MobxTools';
import { IInputMultiSelectFiltersMeta } from './InputMultiSelectFilters';

export interface IDisplayFilterInputProps {
    onBackspace(): void;
    onChange(value: string): void;
    onCheck(option: IFilterOption, meta: IInputMultiSelectFiltersMeta<any>): void;
    isChecked(option: IFilterOption, meta: IInputMultiSelectFiltersMeta<any>): boolean;
    onAddFilter(meta: IInputMultiSelectFiltersMeta<any>): void;
    readonly filterOpen: boolean;
    readonly filter: Mri.DisplayFilters.DisplayFilter;
    readonly filters: readonly IInputMultiSelectFiltersMeta<any>[];
    readonly filterOptions: IDisplayFilterOptions[];
    readonly filtersMeta: readonly IInputMultiSelectFiltersMeta<any>[];
    readonly value: string;
    readonly onlyShowTextSearch?: boolean;
    readonly isInForm?: boolean;
}

export interface IDisplayFilterMeta {
    id: string;
    name: string;
}

export interface IFilterOption {
    title: string;
    value: any;
}

export interface IDisplayFilterOptions {
    name: string;
    options: IFilterOption[];
    meta: IInputMultiSelectFiltersMeta<any> | undefined;
}

@observer
export class DisplayFilterInput extends React.Component<IDisplayFilterInputProps> {
    public constructor(props: IDisplayFilterInputProps) {
        super(props);
       
        this.searchInputValue = props.value || '';
        if(props.filter.displays) {
            this.searchInputValue = props.filter.displays.join(", ");
        }

        const trackedValues = computed(() => [
            this.dropdownOpen,
            this.inputFocused,
            this.filterOpen,
            this.searchInputValue,
        ]);
        componentAutoReaction(this, () => trackedValues.get(), this.reportChange);
    }

    @action
    public componentDidUpdate(prevProps: IDisplayFilterInputProps) {
        if (this.props.value != prevProps.value) {
            if(this.props.filter.displays) {
                this.searchInputValue = this.props.filter.displays.join(", ");
            } else {
                this.searchInputValue = this.props.value;
            }
        }

        if (this.props.filterOpen != prevProps.filterOpen) {
            this.filterOpen = this.props.filterOpen;
        }
    }

    public render() {
        return (
            <Dropdown isOpen={this.dropdownOpen && this.hasOptions} toggle={this.toggleDropdown} className="input-filter-dropdown ps-1">
                <DropdownToggle tag="span" data-toggle="dropdown" aria-expanded={this.dropdownOpen}>
                    <input
                        type="text"
                        className="h-100 scroll-input"
                        placeholder="Search or filter results..."
                        value={this.searchInputValue}
                        onFocus={this.onSearchInputFocus}
                        onBlur={this.onSearchInputBlur}
                        onChange={this.onSearchInputChange}
                        onKeyDown={this.onSearchInputKeyDown}
                        data-selector={`search-bar-internal`}
                    />
                </DropdownToggle>

                <DropdownMenu ref={this.bindDropdownMenuRef} className={this.menuClass}>
                    {!this.searchInputValue &&
                        <InputDropdownItem
                            onClick={this.onSearchButtonClick}
                            title="Press Enter or click to search"
                            icon={<BsSearch style={{ fontSize: 18 }} />}
                            onKeyDown={this.onDropdownItemKeyDown}
                        />
                    }

                    {!this.searchInputValue && this.props.filtersMeta
                        .filter(f => this.shouldRenderFilterMeta(f))
                        .map(f => (
                            <DisplayFilterInputDropdownItem
                                onClick={this.addFilter}
                                meta={f}
                                key={f.id + f.title}
                                onKeyDown={this.onDropdownItemKeyDown}
                            />
                        ))}

                    {this.searchInputValue && this.filterOptionsOnSearch.map((f, k) => (
                        <div key={k + f.name}>
                            {f.meta && f.options.length > 0 && 
                                <DisplayFilterInputDropdownItem
                                    onClick={this.addFilter}
                                    meta={f.meta}
                                    key={f.name + k}
                                    onKeyDown={this.onDropdownItemKeyDown}
                                />
                            }
                            {f.meta && f.options.length > 0 && f.options.map((option: IFilterOption, k: any) =>
                                this.renderEntry(option, f.meta!, option + k)
                            )}
                        </div>
                    ))}
                </DropdownMenu>
            </Dropdown>
        );
    }

    @bind
    @action
    private shouldRenderFilterMeta(meta: IInputMultiSelectFiltersMeta<any>): boolean {
        let metaName= meta.id;
        if(meta.id == 'stateOrProvince') metaName = 'stateOrProvinces'
        
        const selectedValues = this.props.filter[metaName as keyof typeof this.props.filter];
       
        return !(Array.isArray(selectedValues) && selectedValues.length > 0);
    }


    @bind
    @action
    public clearSearchInput() {
        this.searchInputValue = '';
    }

    @bind
    private renderEntry(option: IFilterOption, meta: IInputMultiSelectFiltersMeta<any>, key: any) {
        return (
            <div key={key} className="ps-4 form-check-label f-14">
                <Input className="f-16" type="checkbox" checked={this.props.isChecked(option, meta)}  onChange={() => this.onCheck(option, meta)} />
                <div className="d-inline-block" style={{paddingTop:'2px'}}>{option.title}</div>
            </div>
        );
    }
    
    @bind
    @action
    private onCheck(option: IFilterOption, meta: IInputMultiSelectFiltersMeta<any>) {
        this.props.onCheck(option, meta);
        this.searchInputValue = "";
    }

    @bind
    @action
    private toggleDropdown() {
        if (this.props.onlyShowTextSearch) return;

        this.dropdownOpen = !this.dropdownOpen;
    }

    private getFocusedChild(elements: HTMLCollection): HTMLButtonElement | undefined {
        const active = document.activeElement;
        if (active && includes(elements, active)) return active as HTMLButtonElement;
    }
    
    @bind
    @action
    private onDropdownItemKeyDown(event: React.KeyboardEvent) {
        const dropdownMenu = findDOMNode(this.dropdownMenuRef) as Element;
        const focusedChild = this.getFocusedChild(dropdownMenu.children);

        if (event.key === 'Enter' && focusedChild) {
            focusedChild.click();
        }

        if (event.key === 'ArrowDown' && this.dropdownOpen) {
            const next = (
                focusedChild ? focusedChild.nextElementSibling : dropdownMenu.firstElementChild
            ) as HTMLButtonElement | null;
            if (next) next.focus();
        }

        if (event.key === 'ArrowUp' && this.dropdownOpen) {
            const previous = (
                focusedChild ? focusedChild.previousElementSibling : dropdownMenu.firstElementChild
            ) as HTMLButtonElement | null;
            if (previous) previous.focus();
        }
    }


    @bind
    @action
    private onSearchInputKeyDown(event: React.KeyboardEvent) {
        const { onBackspace } = this.props;

        if (event.key === 'Backspace' && !this.searchInputValue) {
            onBackspace();
        }
        if (event.key === 'Backspace' && this.searchInputValue.length == 1) {
            this.props.onChange("");
        }
        if (event.key === 'Enter') {
            if(this.searchInputValue != ""){
                for (const filterOption of this.filterOptionsOnSearch) {
                    if (filterOption.options.length > 0) {
                        const firstOption = filterOption.options[0];
                        const firstMeta = filterOption.meta!;
                        this.props.onCheck(firstOption, firstMeta);
                        this.searchInputValue = "";
                        break;
                    }
                }
            }
            
            this.dropdownOpen = false;
            this.props.onChange(this.searchInputValue);
        }

        this.onDropdownItemKeyDown(event);
    }


    @bind
    @action
    private onSearchButtonClick() {
        this.dropdownOpen = false;
    }

    @bind
    @action
    private onSearchInputBlur() {
        this.inputFocused = false;
    }

    @bind
    @action
    private onSearchInputFocus() {
        this.inputFocused = true;
        this.filterOpen = false;
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchInputValue = target.value;

        if (!this.props.onlyShowTextSearch) {
            this.dropdownOpen = this.hasOptions;
        }
    }

    @bind
    private reportChange() {
        console.debug(this.dropdownOpen, this.inputFocused, this.filterOpen, this.props.value, this.searchInputValue);
        if (!this.dropdownOpen && !this.inputFocused && !this.filterOpen && this.props.value != this.searchInputValue) {
            console.debug('just gonna send it');

            this.props.onChange(this.searchInputValue);
        }
    }

    @bind
    @action
    private addFilter(meta: IInputMultiSelectFiltersMeta<any>) {
        const { onAddFilter } = this.props;
        this.searchInputValue = '';
        this.filterOpen = true;
        this.dropdownOpen = false;
        onAddFilter(meta);
    }
    
    @computed
    private get filterOptionsOnSearch() {
        let { filterOptions } = this.props;
        let options = filterOptions;

        if (this.searchInputValue) {
            options = options.map(option => ({
                name: option.name,
                options: option.options.filter(opt => deepStringFind(opt, this.searchInputValue)),
                meta: option.meta,
            }));
        }

        return options;
    }
    
    @computed
    private get hasOptions(): boolean {
        return this.filterOptionsOnSearch.some(option => option.options.length > 0);
    }
    
    @computed
    private get menuClass() {
        if(!!this.searchInputValue) {
            return "searching-menu";
        }
    }

    @bind
    private bindDropdownMenuRef(ref: DropdownMenu | null) {
        this.dropdownMenuRef = ref;
    }

    @observable
    private searchInputValue: string;
    @observable
    private dropdownOpen = false;
    @observable
    private inputFocused = false;
    @observable
    private filterOpen = false;
    private dropdownMenuRef: DropdownMenu | null = null;
}

export interface IInputDropdownItemProps {
    onKeyDown?(event: React.KeyboardEvent): void;
    onClick(): void;
    readonly title?: string;
    readonly icon: React.ReactNode;
}

@observer
export class InputDropdownItem extends React.Component<IInputDropdownItemProps> {
    public render() {
        const { icon, title, onClick, onKeyDown } = this.props;
        return (
            <button onClick={onClick} className="filter-dropdown-item py-1 container" onKeyDown={onKeyDown}>
                <div className="row">
                    <div className="col-1">
                        <em>{icon}</em>
                    </div>
                    <div className="col-10">{title}</div>
                </div>
            </button>
        );
    }
}

export interface IDisplayFilterInputDropdownItem {
    onKeyDown?(event: React.KeyboardEvent): void;
    onClick(meta: IInputMultiSelectFiltersMeta<any>): void;
    readonly meta: IInputMultiSelectFiltersMeta<any>;
}

@observer
export class DisplayFilterInputDropdownItem extends React.Component<IDisplayFilterInputDropdownItem> {
    public render() {
        const props = {
            onKeyDown: this.props.onKeyDown,
            onClick: this.onClick,
            title: this.props.meta.title,
            icon: this.props.meta.icon,
        };
        return <InputDropdownItem {...props} />;
    }

    @bind
    private onClick() {
        this.props.onClick(this.props.meta);
    }
}
